import Config from '@/services/Config'

/**
 * The single place the Google Maps script is injected from, and the pieces the
 * maps share once it is: the icons they drop, the controls they draw and the
 * two overlays Leaflet used to give us for free — a popup and a permanent
 * tooltip.
 *
 * The script carries the Places library because the address autocomplete needs
 * it, and a second tag with different parameters would be ignored by Google and
 * warn in the console. So there is one loader, and `GoogleAddressProvider`
 * calls it too.
 */

const SCRIPT_ID = 'google-maps-api'

/**
 * Kept at module scope rather than resolved per call: two maps mounting in the
 * same tick must wait on the same tag, not inject one each.
 * @type {Promise<any>|null}
 */
let loadPromise = null

/**
 * Injects the Google Maps script once and resolves when `google.maps` is
 * usable. Safe to call from anywhere, as often as needed.
 * @function loadGoogleMaps
 * @returns {Promise<any>} The `google.maps` namespace
 */
export function loadGoogleMaps () {
  if (window.google && window.google.maps) return Promise.resolve(window.google.maps)
  if (loadPromise) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID)
    const script = existing || document.createElement('script')

    script.addEventListener('load', () => resolve(window.google.maps))
    script.addEventListener('error', () => {
      /**
       * A failed tag never loads, so it must not be the one the next caller
       * waits on forever.
       */
      loadPromise = null
      if (script.parentNode) script.parentNode.removeChild(script)
      reject(new Error('Could not load the Google Maps script.'))
    })

    if (existing) return

    script.id = SCRIPT_ID
    script.type = 'text/javascript'
    script.async = true
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${Config.get('maps.id')}`
    document.body.appendChild(script)
  })

  return loadPromise
}

/**
 * The options every map shares. Google's own controls are all off: the zoom
 * control is drawn by `addZoomControl` to keep the look the Leaflet maps had,
 * and the rest — Street View, map type, full screen — are doors these maps
 * never opened.
 */
export const BASE_MAP_OPTIONS = {
  disableDefaultUI: true,
  clickableIcons: false,
  keyboardShortcuts: false
}

/**
 * The padding kept around the markers when the camera frames them, in pixels.
 * Leaflet took a single `[x, y]` pair; Google takes each side.
 */
export const fitBoundsPadding = { top: 32, right: 32, bottom: 32, left: 32 }

/** How long the camera animations used to take, still used to time what follows them. */
export const FLY_DURATION = 1.2

const ICON_SIZE = { width: 25, height: 35 }

/**
 * Where the icon touches the map: its point, not its middle, so the pin marks
 * the address rather than hovering over it.
 */
const ICON_ANCHOR = { x: 11, y: 31 }

/**
 * Where a popup's tip lands, relative to the marker's position — Leaflet's
 * `popupAnchor`.
 */
const POPUP_ANCHOR = { x: 4, y: -25 }

/**
 * Builds the icon descriptor for one of the marker images. Google needs its own
 * `Size` and `Point`, so this can only be called once the script has loaded.
 * @function buildIcon
 * @param {string} url
 * @returns {object} icon
 */
function buildIcon (url) {
  return {
    url,
    scaledSize: new window.google.maps.Size(ICON_SIZE.width, ICON_SIZE.height),
    anchor: new window.google.maps.Point(ICON_ANCHOR.x, ICON_ANCHOR.y)
  }
}

/**
 * The offset from a marker's position to the tip of its popup, passed to every
 * marker so the popup sits above the pin instead of over it.
 * @function popupAnchorPoint
 * @returns {object} point
 */
export function popupAnchorPoint () {
  return new window.google.maps.Point(POPUP_ANCHOR.x, POPUP_ANCHOR.y)
}

export const icons = {
  /**
   * Create a pickup icon
   * @function icons.pickup
   * @param {string} state
   * @returns {object} icon
   */
  pickup: (state) => {
    const name = state !== 'scheduled' ? `${state}_pickup` : 'scheduled'

    return buildIcon(require(`@/assets/img/icons/map/${name}.svg`))
  },
  /**
   * Create a delivery icon
   * @function icons.delivery
   * @param {string} state
   * @returns {object} icon
   */
  delivery: (state) => {
    const name = state !== 'scheduled' ? `${state}_delivery` : 'scheduled'

    return buildIcon(require(`@/assets/img/icons/map/${name}.svg`))
  },
  /**
   * Create a disabled icon
   * @function icons.disabled
   * @returns {object} icon
   */
  disabled: () => buildIcon(require('@/assets/img/icons/map/disabled.svg'))
}

/**
 * Draws the zoom control the maps used to get from Leaflet: two stacked
 * buttons, bottom right, each greyed out once the map cannot go further that
 * way. Google's own control is a different object with a different shape, so
 * this is a plain element handed to the map's control stack.
 * The labels are the ones Leaflet gave its own buttons, in English and
 * untranslated, so nothing the user reads changes with the library.
 * @function addZoomControl
 * @param {any} map
 * @returns {HTMLElement} control
 */
export function addZoomControl (map) {
  const labels = { in: 'Zoom in', out: 'Zoom out' }

  const control = document.createElement('div')
  control.className = 'ctk-map-zoom'

  /**
   * @param {string} modifier
   * @param {number} step
   * @returns {HTMLButtonElement}
   */
  const addButton = (modifier, step) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = `ctk-map-zoom__button ctk-map-zoom__button--${modifier}`
    button.textContent = step > 0 ? '+' : '−'
    button.setAttribute('aria-label', labels[modifier])
    button.setAttribute('title', labels[modifier])
    button.addEventListener('click', () => {
      map.setZoom((map.getZoom() || 0) + step)
    })
    control.appendChild(button)

    return button
  }

  const zoomIn = addButton('in', 1)
  const zoomOut = addButton('out', -1)

  /**
   * Leaflet greyed its buttons out at the limits; without this the buttons stay
   * lit and press to nothing once the map is as close in as it goes.
   */
  const refresh = () => {
    const zoom = map.getZoom()
    const maximum = map.get('maxZoom')
    const minimum = map.get('minZoom')

    zoomIn.classList.toggle('ctk-map-zoom__button--disabled', typeof maximum === 'number' && zoom >= maximum)
    zoomOut.classList.toggle('ctk-map-zoom__button--disabled', typeof minimum === 'number' && zoom <= minimum)
  }

  map.addListener('zoom_changed', refresh)
  refresh()

  map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(control)

  return control
}

/**
 * The popup a marker opens when clicked, with the close button and the white
 * frame taken off in `ctk-map.scss` so what shows is the coloured card the
 * Leaflet popups drew.
 * @function createPopup
 * @param {string} content - Trusted HTML, built by the map from its own data
 * @returns {any} infoWindow
 */
export function createPopup (content) {
  return new window.google.maps.InfoWindow({
    content: `<div class="ctk-map-popup">${content}</div>`,
    disableAutoPan: true
  })
}

/**
 * Binds a popup to a marker, opening it on click and closing it on a second
 * one — Leaflet's behaviour, which the maps' users already know.
 * @function bindPopup
 * @param {any} map
 * @param {any} marker
 * @param {string} content
 * @returns {any} infoWindow
 */
export function bindPopup (map, marker, content) {
  const popup = createPopup(content)
  let isOpen = false

  marker.addListener('click', () => {
    if (isOpen) {
      popup.close()
      isOpen = false

      return
    }

    popup.open({ map, anchor: marker })
    isOpen = true
  })

  popup.addListener('closeclick', () => {
    isOpen = false
  })

  return popup
}

/**
 * The permanent labels the shipment map hangs off its markers. Leaflet had
 * tooltips; Google has nothing of the sort, so this is the smallest overlay
 * that places an element at a position and lets the stylesheet draw it.
 *
 * Built lazily because `OverlayView` only exists once the script has loaded.
 * @function createTooltip
 * @param {any} map
 * @param {object} options
 * @param {object} options.position - The `{ lat, lng }` the tooltip points at
 * @param {string} options.content - Trusted HTML
 * @param {string} options.className
 * @param {string} options.direction - 'left', 'right' or 'top'
 * @param {Array<number>} options.offset - `[x, y]` from the position, in pixels
 * @returns {any} overlay
 */
export function createTooltip (map, { position, content, className, direction, offset }) {
  const overlay = new window.google.maps.OverlayView()

  overlay.onAdd = function () {
    this.element = document.createElement('div')
    this.element.className = `ctk-map-tooltip ctk-map-tooltip--${direction} ${className || ''}`
    this.element.innerHTML = content
    this.getPanes().floatPane.appendChild(this.element)
  }

  overlay.draw = function () {
    if (!this.element) return

    const point = this.getProjection()
      .fromLatLngToDivPixel(new window.google.maps.LatLng(position.lat, position.lng))
    if (!point) return

    this.element.style.left = `${point.x + offset[0]}px`
    this.element.style.top = `${point.y + offset[1]}px`
  }

  overlay.onRemove = function () {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element)
      this.element = null
    }
  }

  overlay.setMap(map)

  return overlay
}

/**
 * Frames the map on everything it was given. A single position has no span to
 * frame, so it gets a centre and a chosen zoom instead.
 * @function fitToPositions
 * @param {any} map
 * @param {Array<object>} positions - `{ lat, lng }` literals
 * @param {object} [options]
 * @param {object} [options.padding]
 * @param {number} [options.singlePositionZoom]
 */
export function fitToPositions (map, positions, options = {}) {
  if (!positions.length) return

  const { padding = fitBoundsPadding, singlePositionZoom = 14 } = options

  if (positions.length === 1) {
    map.setCenter(positions[0])
    map.setZoom(singlePositionZoom)

    return
  }

  const bounds = new window.google.maps.LatLngBounds()
  positions.forEach(position => bounds.extend(position))

  map.fitBounds(bounds, padding)
}
